import { NewChatPromo } from "@/components/landing/NewChatPromo";
import { WhatsAppPromo } from "@/components/landing/WhatsAppPromo";
import { EarnTiles } from "@/components/landing/EarnTiles";
import { ExplainerVideo } from "@/components/landing/ExplainerVideo";
import { NewGames } from "@/components/landing/NewGames";
import { HeroBackdrop } from "@/components/landing/HeroBackdrop";
import { LiveCommunity } from "@/components/landing/LiveCommunity";
import { FunShowcase } from "@/components/landing/FunShowcase";
import { HalloweenBadge } from "@/components/HalloweenLayer";
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Gift, LogIn, Sparkles, Trophy, Crown } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { FacebookWelcome } from "@/components/FacebookWelcome";
import { InstallPrompt } from "@/components/InstallPrompt";
import { DesktopQr } from "@/components/DesktopQr";
import { HeroMessage } from "@/components/landing/AuthHero";
import { LoginForm } from "@/pages/Auth";
import { MemberVoices, TopRewards } from "@/components/landing/TrustBits";
import { Features, RewardsShowcase, GiftShop } from "@/components/landing/Sections";
import { LegalLinks } from "@/pages/Legal";
import { usePageMeta } from "@/lib/seo";
import { useCountUp } from "@/components/PointsBurst";
import { H2 } from "@/components/landing/Kit";

const NAV = [["#video", "So geht's"], ["#spiele", "Spiele"], ["#neu", "Neu"], ["#geschenke", "Geschenke"], ["#netzwerk", "Community"], ["#punkte", "Punkte"]];

const PublicHeader = () => (
  <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-pink-100">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
      <Logo to="/" />
      <nav className="hidden md:flex items-center gap-1">{NAV.map(([h, l]) => <a key={h} href={h} className="px-3 py-2 rounded-full text-sm font-semibold text-slate-600 hover:text-rose-600 hover:bg-rose-50">{l}</a>)}<Link to="/ruhmeshalle" data-testid="landing-nav-hof" className="px-3 py-2 rounded-full text-sm font-semibold text-amber-600 hover:bg-amber-50 inline-flex items-center gap-1"><Trophy className="w-4 h-4" /> Ruhmeshalle</Link></nav>
      <div className="flex items-center gap-2">
        <a href="#login" data-testid="landing-login-btn" className="inline-flex items-center gap-1.5 px-3 sm:px-4 h-10 rounded-full text-sm font-semibold text-slate-700 hover:bg-rose-50"><LogIn className="w-4 h-4" /> Login</a>
        <Link to="/registrieren" data-testid="landing-header-register-btn" className="hidden sm:inline-flex items-center px-4 h-10 rounded-full pink-gradient text-white text-sm font-bold shadow-md shadow-rose-200">Mitglied werden</Link>
      </div>
    </div>
  </header>
);

const Hero = ({ stats }) => (
  <section className="relative overflow-hidden bg-gradient-to-b from-[#FFF5F8] via-white to-white" data-testid="landing-hero">
    <HeroBackdrop />
    <div className="absolute -top-48 -left-40 w-[40rem] h-[40rem] rounded-full bg-rose-200/40 blur-3xl pointer-events-none" />
    <div className="absolute top-20 -right-48 w-[36rem] h-[36rem] rounded-full bg-fuchsia-200/30 blur-3xl pointer-events-none" />
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 lg:pt-16 pb-14 lg:pb-20 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center lg:min-h-[calc(100vh-4rem)]">
      <div className="lg:col-span-7 fade-up">
        <HalloweenBadge className="mb-4" />
        <HeroMessage stats={stats || null} />
      </div>
      <div id="login" className="lg:col-span-5 flex flex-col items-center lg:items-end gap-5 scroll-mt-24">
        <div className="w-full max-w-md bg-white rounded-[2rem] border border-rose-100 p-6 sm:p-8 space-y-5 glow-card" data-testid="landing-login-card">
          <LoginForm />
        </div>
        <MemberVoices />
      </div>
    </div>
  </section>
);

const agoDe = (d) => {
  const m = Math.max(1, Math.round((Date.now() - new Date(d)) / 60000));
  return m < 60 ? `vor ${m} Min.` : m < 1440 ? `vor ${Math.round(m / 60)} Std.` : `vor ${Math.round(m / 1440)} ${Math.round(m / 1440) === 1 ? "Tag" : "Tagen"}`;
};

const MONTH_DE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];

const GoalBar = ({ stats }) => {
  const pct = Math.min(100, Math.round((stats.points / stats.goal) * 100));
  const [w, setW] = useState(0);
  useEffect(() => { const t = setTimeout(() => setW(pct), 200); return () => clearTimeout(t); }, [pct]);
  return (
    <div data-testid="landing-goal">
      <div className="flex items-center justify-between gap-3 text-xs font-bold">
        <span>{stats.reached ? `🎉 Community-Ziel erreicht – alle Mitglieder bekamen +${stats.bonus} Punkte!` : `🎯 Community-Ziel: ${stats.goal.toLocaleString("de-DE")} Punkte → +${stats.bonus} für alle`}</span>
        <span className="tabular-nums shrink-0" data-testid="landing-goal-percent">{pct} %</span>
      </div>
      <div className="mt-1.5 h-3 rounded-full bg-white/30 overflow-hidden">
        <div className="h-full rounded-full bg-white shadow-[0_0_12px_rgba(255,255,255,.9)] transition-[width] duration-[1800ms] ease-out" style={{ width: `${w}%` }} />
      </div>
    </div>
  );
};

const WheelCounter = ({ stats }) => {
  const [target, setTarget] = useState(0);
  useEffect(() => { setTarget(stats.points); }, [stats.points]);
  const pts = useCountUp(target, 1800);
  return (
    <div className="rounded-2xl p-4 sm:px-6 bg-gradient-to-r from-amber-300 via-orange-400 to-pink-500 text-white shadow-lg shadow-orange-200 space-y-3" data-testid="landing-wheel-counter">
     <div className="flex items-center gap-4">
      <span className="w-12 h-12 shrink-0 rounded-2xl bg-white/25 flex items-center justify-center text-2xl">🎡</span>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-extrabold uppercase tracking-widest text-white/85">Glücksrad im {MONTH_DE[new Date().getMonth()]}</p>
        <p className="font-display text-2xl sm:text-3xl font-black leading-tight"><span className="tabular-nums" data-testid="landing-wheel-points">{pts.toLocaleString("de-DE")}</span> Punkte gewonnen</p>
      </div>
      <div className="hidden sm:flex gap-5 text-right order-last">
        <div><p className="font-display text-xl font-extrabold tabular-nums" data-testid="landing-wheel-spins">{stats.spins.toLocaleString("de-DE")}</p><p className="text-[11px] font-bold text-white/85">Drehungen</p></div>
        <div><p className="font-display text-xl font-extrabold tabular-nums" data-testid="landing-wheel-jackpots">{stats.jackpots}</p><p className="text-[11px] font-bold text-white/85">Jackpots</p></div>
      </div>
     </div>
     {stats.goal > 0 && <GoalBar stats={stats} />}
    </div>
  );
};

const JackpotTicker = () => {
  const [wins, setWins] = useState([]);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const load = () => {
      api.get("/public/jackpots").then(({ data }) => setWins(data)).catch(() => {});
      api.get("/public/wheel-stats").then(({ data }) => setStats(data)).catch(() => {});
    };
    load();
    const iv = setInterval(() => document.visibilityState === "visible" && load(), 60000);
    return () => clearInterval(iv);
  }, []);
  if (!wins.length && !stats?.points && !stats?.goal) return null;
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 -mt-6 mb-4 space-y-3" data-testid="landing-jackpot-ticker">
      {(stats?.points > 0 || stats?.goal > 0) && <WheelCounter stats={stats} />}
      {wins.length > 0 && (
        <Link to="/registrieren" className="relative block overflow-hidden rounded-2xl bg-slate-900 text-white border border-amber-400/40 shadow-lg shadow-amber-200/40">
          <div className="absolute left-0 inset-y-0 z-10 flex items-center gap-1.5 pl-4 pr-3 bg-slate-900 shadow-[14px_0_14px_-2px_#0f172a] text-[10px] font-extrabold uppercase tracking-widest text-amber-300">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" /> Live-Jackpots
          </div>
          <div className="flex gap-10 py-3 pl-36 w-max ticker-run" style={{ animationDuration: `${Math.max(14, wins.length * 7)}s` }}>
            {[...wins, ...wins].map((w, i) => (
              <span key={i} className="inline-flex items-center gap-2 text-sm whitespace-nowrap" data-testid={i < wins.length ? `landing-jackpot-${i}` : undefined}>
                <Crown className="w-4 h-4 text-amber-400 fill-amber-400" /> <b>{w.name}</b> hat den Glücksrad-Jackpot geknackt: <b className="text-amber-300">+{w.points} Punkte</b> <span className="text-xs text-white/50">{agoDe(w.created_at)}</span>
              </span>
            ))}
          </div>
        </Link>
      )}
    </section>
  );
};

const HallTeaser = () => {
  const [latest, setLatest] = useState(undefined);
  useEffect(() => { api.get("/public/hall-of-fame").then(({ data }) => setLatest(data[0] || null)).catch(() => setLatest(null)); }, []);
  const people = latest ? [...latest.winners, ...(latest.climber ? [{ ...latest.climber, climber: true }] : [])] : [];
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-8" data-testid="landing-hof-teaser">
      <Link to="/ruhmeshalle" data-testid="landing-hof-link" className="group relative overflow-hidden flex flex-col sm:flex-row sm:items-center gap-6 rounded-[2rem] p-6 sm:p-8 bg-slate-900 text-white hover:-translate-y-0.5 transition-transform">
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-amber-400/20 blur-2xl" />
        <span className="relative w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-br from-amber-300 to-orange-500 flex items-center justify-center"><Crown className="w-7 h-7 text-white" /></span>
        <div className="relative flex-1 min-w-0">
          <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-amber-300">Ruhmeshalle</p>
          <h2 className={`${H2} mt-1`}>{latest ? `Die Legenden vom ${latest.label}` : "Hier werden unsere Legenden verewigt"}</h2>
          <p className="text-sm text-white/70 mt-1">Jeden Monat kommen die Top 3 Punktesammler und der größte Level-Aufsteiger in die Ruhmeshalle.</p>
        </div>
        {people.length > 0 && (
          <div className="relative flex -space-x-3">
            {people.map((p, i) => p.picture
              ? <img key={i} src={p.picture} alt={p.name} className="w-12 h-12 rounded-full object-cover ring-4 ring-slate-900" />
              : <span key={i} className="w-12 h-12 rounded-full ring-4 ring-slate-900 bg-gradient-to-br from-rose-400 to-fuchsia-500 flex items-center justify-center font-extrabold">{p.name?.[0]?.toUpperCase()}</span>)}
          </div>
        )}
        <span className="relative inline-flex items-center gap-1.5 text-sm font-bold text-amber-300 group-hover:gap-2.5 transition-all">Ansehen <ArrowRight className="w-4 h-4" /></span>
      </Link>
    </section>
  );
};

const FinalCta = () => (
  <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
    <div className="relative overflow-hidden rounded-[2.5rem] p-8 sm:p-14 text-white bg-gradient-to-br from-rose-500 via-pink-500 to-fuchsia-500 shadow-2xl shadow-rose-200">
      <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-white/10" />
      <h2 className={`${H2}  relative max-w-2xl sm:text-5xl`}>Bereit für deine erste Runde?</h2>
      <p className="relative mt-4 max-w-xl text-pink-50">Kostenlos registrieren, App installieren, losspielen – und ab dem ersten Tag Punkte für echte Prämien sammeln.</p>
      <Link to="/registrieren" data-testid="landing-final-cta" className="relative mt-8 inline-flex items-center gap-2 px-7 h-14 rounded-full bg-white text-rose-600 font-extrabold text-sm tracking-wide hover:scale-[1.03] transition-transform">JETZT KOSTENLOS MITGLIED WERDEN <ArrowRight className="w-4 h-4" /></Link>
    </div>
  </section>
);

const LandingFooter = () => (
  <footer className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 pt-4" data-testid="landing-footer">
    <div className="rounded-3xl bg-white/80 border border-pink-100 px-5 py-6 flex flex-col sm:flex-row sm:flex-wrap items-center justify-between gap-4 text-xs text-slate-400">
      <Logo to="/" /><nav className="flex flex-wrap justify-center gap-x-4 gap-y-1 font-semibold text-slate-500"><Link to="/app" data-testid="landing-footer-app" className="hover:text-rose-600">App</Link><Link to="/ruhmeshalle" data-testid="landing-footer-hof" className="hover:text-rose-600">Ruhmeshalle</Link><Link to="/links" data-testid="landing-footer-links" className="hover:text-rose-600">Links</Link><Link to="/projekt-unterstuetzen" data-testid="landing-footer-support" className="hover:text-rose-600">Projekt unterstützen</Link></nav>
      <LegalLinks className="justify-center font-bold text-slate-600" />
      <span className="sm:basis-full text-center">© {new Date().getFullYear()} itzPlay · Spielen. Punkte sammeln. Prämien einlösen.</span>
    </div>
  </footer>
);

export default function Landing() {
  usePageMeta({ description: "itzPlay: Spiele wie Herz-Läufer, Zuckerherz Saga und Zwei Herzen spielen, Punkte sammeln und gegen echte Prämien einlösen – kostenlos." });
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [layout, setLayout] = useState(null);
  useEffect(() => { api.get("/public/landing-layout").then(({ data }) => setLayout(data)).catch(() => setLayout({ order: ["hero", "jackpots", "live", "games", "gifts", "chat", "whatsapp", "network", "points", "hall", "cta"], hidden: [] })); }, []);
  useEffect(() => {
    let gone = false;
    const get = (path, set, tries = 4) => api.get(path).then(({ data }) => !gone && set(data))
      .catch(() => tries > 1 && !gone && setTimeout(() => get(path, set, tries - 1), 2000));
    get("/public/stats", setStats);
    get("/public/rewards", setRewards);
    return () => { gone = true; };
  }, []);
  if (user) return <Navigate to="/feed" replace />;
  return (
    <div className="min-h-screen noise-bg landing-glow" data-testid="landing-page">
      <PublicHeader />
      <FacebookWelcome />
      <InstallPrompt />
      <DesktopQr variant="float" />
      {layout && layout.order.filter((k) => !layout.hidden.includes(k)).map((k) => {
        const S = {
          hero: <><Hero stats={stats} /><TopRewards /></>, jackpots: <JackpotTicker />, live: <LiveCommunity />, video: <ExplainerVideo />, games: <NewGames />, whatsapp: <WhatsAppPromo />, fun: <FunShowcase />, gifts: <GiftShop rewards={rewards} user={user} />,
          chat: <NewChatPromo />, earn: <EarnTiles />, network: <Features />, points: <RewardsShowcase settings={stats?.settings} reward={rewards?.[rewards.length - 1]} />,
          hall: <HallTeaser />, cta: <FinalCta />,
        }[k];
        return S ? <div key={k} data-section={k}>{S}</div> : null;
      })}
      <LandingFooter />
    </div>
  );
}
